import { Geography, State, Country, WorldRegion, GeoTreeNode, EntGeographyList, InvestorGeographyList, allRegions } from './geography';
import { Industry, IndustryOption, InvestorIndustryOptionArray, EntIndustryOptionArray, InvestorIndustryList, EntIndustryList } from './industry';
import { ErrorCode } from './error';

export type Optional<T> = T | null;

export interface MyUser {
  id: number
  name: string
  email: string
  auth0Id: string
  role: Role
  profileId: number
}

export interface FileUploadUrl {
  postUrl: string
}
export interface ConnectorProfile {
  id: number
  firstName: string
  lastName: string
  email: string
  companyName: string
  linkedInProfileUrl: string
  introduction: string
  profileImageUrl: string
  referralExpectation: string
  entrepreneurRevenueRequiredLow: number
  editable: boolean
}

export interface ConnectorDealReferral {
  id: number
  contactName: string
  companyName: string
  profileImage: string
  status: ReferralStatus
  investorProfileId: number
}

export interface ConnectorDeal {
  id: number
  entrepreneurProfileId: number
  intro: string
  logo: string
  profileImage: string
  website: string
  email: string
  contactName: string
  contactTitle: string
  companyName: string
  marketModelList: MarketModel[]
  financeSoughtLow: number
  financeSoughtHigh: number
  financeRaised: number
  currentRevenue: number
  status: ConnectorDealStatus
  referrals: ConnectorDealReferral[]
  financialProjection: Projection[]
  geography: Geography
  industryList: string[]
  matches: BasicMatchedInvestor[]
  totalMatchCount: number
  referralRequestedDate?: string
}


export enum Role {
  Ent = 'entrepreneur',
  Connector = 'connector',
  Investor = 'investor'
}

export enum ProfileAccessLevel {
  Anonymous = 'Anonymous',
  AuthedOwner = 'AuthedOwner',
  AuthedNonOwner = 'AuthedNonOwner'
}

export interface PitchDoc {
  srcUrl: string
  type: string // video || pdf || slides || linkedin
}

export interface MarketModelCriteria {
  B2B: 'Only' | 'Sometimes' | 'Never' | null
  B2C: 'Only' | 'Sometimes' | 'Never' | null
  B2G: 'Only' | 'Sometimes' | 'Never' | null
  SaaS: 'Only' | 'Sometimes' | 'Never' | null
}

export interface InvestorProfile {
  id: number
  contactName: string
  companyName: string
  profileImage: string
  linkedinProfile: string
  intro: string
  email: string
  phone: string
  website: string
  addressStreet1: string
  addressStreet2: string
  addressCity: string
  addressState: string
  addressCountry: string
  investmentLow: number
  investmentHigh: number
  investmentRevenue: number
  founderFemale: boolean
  founderUnderrepresented: boolean
  industryList: Industry[]
  geographyList: Geography[]
  marketModelCriteria: MarketModelCriteria
  matchScore?: number
  connectorList?: ConnectorData[],
  isPublished: boolean,
  otherCriteria: string,
  editable: boolean,
  anonymized: boolean
}

// ConnectorData is used for the matched investor list with connector data attached
export interface ConnectorData {
  id: number
  firstName: string
  lastName: string
  companyName: string
  email: string
  profileImage?: string
  investorCount?: number
  matchCount?: number
  referralData?: ReferralData
}

export interface ReferralData {
  status: ReferralStatus,
  entMessage?: string
}

export interface InvestorOnboardBody {
  email: string
}

export type InvestorIntro = Pick<InvestorProfile, 'contactName' | 'companyName' | 'intro' | 'linkedinProfile' | 'email'>;
export type InvestorInvestmentCriteria = Pick<InvestorProfile, 'investmentLow' | 'investmentHigh' | 'investmentRevenue' | 'founderFemale' | 'founderUnderrepresented' | 'marketModelCriteria' | 'industryList' | 'geographyList'>;
export type InvestorOtherCriteria = string;

export interface InvestorUpdateBody {
  intro?: InvestorIntro
  investmentCriteria?: InvestorInvestmentCriteria
  otherCriteria?: InvestorOtherCriteria
  isPublished?: boolean
}
export interface CreateEntrepreneurProfileBody {
  name: string
}

export interface Projection {
  id: number
  year: number
  revenue: number
  EBITDA: number
}

export const defaultProjections: Projection[] = [0, 1, 2, 3, 4].map(() => ({
  id: 0,
  year: 0,
  revenue: 0,
  EBITDA: 0
}));

export interface Management {
  id?: number | null
  title: string
  name: string
  intro?: string
}

export const defaultManagement: Management[] = ['CEO', 'CTO'].map((i) => ({ name: '', title: i, intro: '' }));

export interface EntrepreneurProfileBody {
  id: number
  name: string
  intro: string
  phone: string
  website: string
  email: string
  contactName: string
  contactTitle: string
  street1: string
  street2: string
  city: string
  state: string
  country: string
  zipCode: string
  geography: Geography
  description: string
  background: string
  productService: string
  technology: string
  market: string
  distribution: string
  management: string
  competition: string
  marketModelList: MarketModel[]
  currentInvestor: string
  fundUsage: string
  founderFemale: boolean
  founderExit: boolean
  founderUnderrepresented: boolean
  lawFirm: string
  capTableManagement: string
  hrProvider: string
  bank: string
  auditor: string
  employeeCount?: number
  financialProjection: Projection[]
  managementTeam: Management[]
  financeRaised: number
  financeSoughtHigh: number
  financeSoughtLow: number
  year5Revenue: number
  currentRevenue: number
  industryList: Industry[]
  elevatorPitchUrl: string
  fullPitchUrl: string
  pitchDeckUrl: string
  executiveSummaryUrl: string
  linkedinProfileUrl: string
  isPublished: boolean
  profileImageUrl: string
  logo: string
  editable: boolean
  hasDeals?: boolean
}

export type InvestorMatchInput = Pick<InvestorProfile, 'id' | 'email' | 'linkedinProfile' | 'intro' | 'profileImage' | 'companyName' | 'contactName' | 'investmentHigh' | 'investmentLow' | 'investmentRevenue' | 'geographyList' | 'industryList' | 'founderFemale' | 'founderUnderrepresented' | 'marketModelCriteria' | 'otherCriteria'>;
export interface InvestorMatchOutput extends InvestorMatchInput {
  matchScore: number
  referralStatus?: ReferralStatus
  entMessage?: string
  connectorList?: ConnectorData[]
}

export type EntrepreneurIntro = Pick<EntrepreneurProfileBody,
'name' | 'intro' | 'geography' |
'industryList' | 'marketModelList' |
'linkedinProfileUrl' | 'elevatorPitchUrl' | 'pitchDeckUrl' | 'executiveSummaryUrl' | 'fullPitchUrl'
>;

export type EntrepreneurContact = Pick<EntrepreneurProfileBody,
'contactName' | 'contactTitle' | 'street1' | 'street2' | 'state' | 'city' | 'country' | 'zipCode' | 'phone' | 'website' | 'email'
>;

export type EntrepreneurFinance = Pick<EntrepreneurProfileBody,
'financeSoughtHigh' | 'financeSoughtLow' | 'financeRaised' | 'currentRevenue' | 'financialProjection'
>;

export type EntrepreneurExecutiveSummary = Pick<EntrepreneurProfileBody,
'background' | 'management' | 'market' | 'competition' | 'distribution' | 'financialProjection'
>;

export type EntrepreneurCompanyDetails = Pick<EntrepreneurProfileBody,
'managementTeam' | 'currentInvestor' | 'bank' | 'lawFirm' | 'fundUsage' | 'auditor' | 'employeeCount' | 'founderExit' | 'founderFemale' | 'founderUnderrepresented' | 'capTableManagement' | 'hrProvider'
>;

export type EntrepreneurFinanceUpdateBody = {
  financeRaised?: number | undefined | null;
  financeSoughtHigh?: number | undefined | null;
  financeSoughtLow?: number | undefined | null;
  currentRevenue?: number | undefined | null;
};

export interface EntrepreneurUpdateBody {
  intro?: EntrepreneurIntro
  contact?: EntrepreneurContact
  finance?: EntrepreneurFinanceUpdateBody
  companyDetails?: EntrepreneurCompanyDetails
  executiveSummary?: EntrepreneurExecutiveSummary
  isPublished?: boolean
}

export enum TypographyColor {
  initial = 'initial',
  inherit = 'inherit',
  primary = 'primary',
  secondary = 'secondary',
  textPrimary = 'textPrimary',
  textSecondary = 'textSecondary',
  error = 'error'
}

export interface FileUploadResult {
  data: string | ArrayBuffer,
  url: string,
  mime: string
}

export interface ImageProvider {
  updateImage?: (image: FileUploadResult) => Promise<void>,
  imgUrl: string,
  tempImgUrl?: string | null,
  editable: boolean,
  isSelectingImage?: boolean,
  setIsSelectingImage?: (isSelecting: boolean) => void
}

export enum MarketModel {
  B2B = 'B2B',
  B2C = 'B2C',
  B2G = 'B2G',
  SaaS = 'SaaS'
}

export const marketModelList: MarketModel[] = [MarketModel.B2B, MarketModel.B2C, MarketModel.B2G, MarketModel.SaaS];

export interface UpdateConnectorDealBody {
  status: ConnectorDealStatus
}

export enum Orientation {
  Horizontal = 0,
  Vertical = 1
}

export enum Shape {
  Rect = 0,
  Round = 1
}

export enum UserAssetType {
  Avatar = 'avatar',
  Logo = 'logo'
}

export interface BasicProfileData {
  name: string,
  profileImgUrl: string,
  company: string,
}

export interface ConnectorReferralData {
  id: number,
  entId: number,
  status: string,
  intro: string,
  connector: BasicProfileData
}

export interface SendReferralsResponse {
  success: boolean,
  dealStatusUpdated: boolean
}

export interface GetEntProfileResponse {
  profile: EntrepreneurProfileBody,
  referral?: ConnectorReferralData
}

export interface EntrepreneurAskReferralRequest {
  connectorProfileId: number
  entMessage: string
  investorsRequested: number[]
}
export interface DealInfoForTopbar {
  forEnt?: number
  loadingStatus: LoadingStatus
  deal?: ConnectorDeal
}

export enum ConnectorDealStatus {
  New = 'new',
  InProgress = 'in_progress',
  Done = 'done'
}

export enum MatchStatus {
  recommend = 'recommend',
  sent = 'sent'
}

export interface ConnectorDealsResponseType {
  deals: ConnectorDeal []
  total: number
  from: number
  count: number
  matches: Match []
}
export interface Match {
  entId: number
  totalInvestorCount: number
  scoredInvestors: BasicMatchedInvestor []
}
export interface BasicMatchedInvestor {
  companyName: string
  contactName: string
  email: string
  id: number
  profileImage: string
  matchScore: number
}

// to-do: create matched investor type that extends BasicMatchedInvestor and use as response type from matched investors endpoint to make these mappings unnecessary
// or otherwise consolidate types

export function basicMatchedInvestorFromInvestorProfile(inv: InvestorProfile): BasicMatchedInvestor {
  const { companyName, contactName, email, id, profileImage } = inv;
  const matchScore = inv.matchScore || 0.01;
  return { companyName, contactName, email, id, profileImage, matchScore };
}

export function connectorReferralOptionFromInvestorProfile(inv: InvestorMatchOutput): ConnectorReferralOption {
  const { companyName, contactName, email, id, profileImage, matchScore } = inv;
  return { companyName, contactName, email, investorProfileId: id, profileImage, matchScore: matchScore || 0.01 };
}

export interface EntReferralOption {
  investorProfileId: number
  profileImage?: string
  matchScore: number
  companyName: string
  contactName: string
  referralStatus?: ReferralStatus
}
export interface ConnectorReferralOption extends EntReferralOption {
  email: string
}

export interface ConnectorSendReferralRequest {
  dealId?: number
  entId?: number
  investors: { name:string, email:string, id:number }[]
  additionalRecipients: string[]
  message: string
}

export enum ReferralStatus {
  Requested = 'requested',
  Pending = 'pending',
  Accepted = 'accepted',
  Declined = 'declined'
}

export enum InvestorReferralResponse {
  Accept = 'accept',
  Decline = 'decline'
}

export interface InvestorReferralReplyBody {
  referralId: string
  referralResponse: InvestorReferralResponse
  message: string
}

export enum LoadingStatus {
  Idle = 'idle',
  Loading = 'loading',
  Complete = 'complete'
}

export enum MatchListType {
  recommended = 'recommended',
  sent = 'sent',
  requested = 'requested'
}

export interface MatchedInvestorListMetadataTabInput {
  listType: MatchListType,
  filters: FilterOption[],
}
export interface MatchedInvestorListBody {
  requested?: MatchedInvestorListMetadataTabInput,
  recommended?: MatchedInvestorListMetadataTabInput,
  sent?: MatchedInvestorListMetadataTabInput
}

export type GetMatchedInvestorsForEntreprepeur = {
  entId: number,
  query: string,
  body: MatchedInvestorListBody
};

export interface EntMatchedInvestorsResponse {
  data: InvestorMatchOutput[]
  metadata: { requested: number, recommended: number, sent: number }
}

export type InvestorData = Pick<InvestorProfile, 'id' | 'isPublished' | 'companyName' | 'contactName' | 'email' | 'linkedinProfile' | 'profileImage'>;
export interface UserNetwork {
  networkId: number
  requesterConnectorProfileId: number
  targetInvestorProfileId: number
  investor: InvestorData
}

export interface ConnectorNetworkResponseType {
  network: UserNetwork[]
  total: number
  from: number
  count: number
}

export interface AddInvestorToNetworkResponse {
  networkRelationshipCreated: boolean,
  isDeleted: boolean,
  newProfileCreated: boolean,
  unpublished: boolean,
  profileId: number
}

export interface FilterOption {
  key: string
  value: any
  label: string
  query: string
}


export {
  EntGeographyList, InvestorGeographyList, allRegions, WorldRegion, State, Country,
  EntIndustryOptionArray, InvestorIndustryOptionArray, EntIndustryList, InvestorIndustryList, ErrorCode, IndustryOption
};

export type {
  Geography, GeoTreeNode,
  Industry
};

export enum ConnectorTab {
  New = 'new',
  InProgress = 'inprogress',
  Done = 'done'
}

export const PAGE_SIZE = 10; // this is a constant, not a type. fix later
